import type { InterfaceLanguageOptionDto } from '@asset-metrix/portal-gateway-client-js';
import i18n from 'i18next';

import { capitalizeFirstLetter } from '../utils/capitalizeFirstLetter';

export const PREFERRED_LANGUAGE_STORAGE_KEY = 'preferred-language';
export const DEFAULT_LANGUAGE = 'en';

function getSupportedLanguageCodes(): string[] {
  return Object.keys(i18n.options.resources ?? {});
}

/**
 * While i18next has no resource bundles registered we cannot tell which
 * languages exist, so every language is accepted instead of forcing the user
 * back to the default.
 */
export function isSupportedLanguage(
  language: string,
  supportedLanguages: string[] = getSupportedLanguageCodes()
): boolean {
  return (
    supportedLanguages.length === 0 || supportedLanguages.includes(language)
  );
}

export function readStoredLanguage(): string | undefined {
  try {
    const raw = globalThis.localStorage.getItem(PREFERRED_LANGUAGE_STORAGE_KEY);
    if (raw == null || raw === '') {
      return undefined;
    }

    try {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed === 'string' && parsed.length > 0) {
        return parsed;
      }
    } catch {
      if (raw.length > 0) {
        return raw;
      }
    }
  } catch {
    return undefined;
  }

  return undefined;
}

export function setPreferredLanguage(language: string): void {
  try {
    globalThis.localStorage.setItem(
      PREFERRED_LANGUAGE_STORAGE_KEY,
      JSON.stringify(language)
    );
  } catch (error) {
    console.warn('Could not persist the preferred language', error);
  }
}

export function clearPreferredLanguage(): void {
  try {
    globalThis.localStorage.removeItem(PREFERRED_LANGUAGE_STORAGE_KEY);
  } catch (error) {
    console.warn('Could not clear the preferred language', error);
  }
}

export function resolvePreferredLanguage(
  localeMessages?: string | null,
  supportedLanguages: string[] = getSupportedLanguageCodes()
): string {
  const storedLanguage = readStoredLanguage();

  return (
    (storedLanguage != null &&
    isSupportedLanguage(storedLanguage, supportedLanguages)
      ? storedLanguage
      : undefined) ??
    (localeMessages && localeMessages.length > 0
      ? localeMessages
      : undefined) ??
    DEFAULT_LANGUAGE
  );
}

function getLanguageLabel({
  displayName,
  systemLanguage,
}: InterfaceLanguageOptionDto): string {
  // `i18n.language` is undefined until i18next is initialized.
  const locale = (i18n.language as string | undefined) ?? systemLanguage;
  try {
    const name = new Intl.DisplayNames([locale], {
      type: 'language',
      fallback: 'none',
    }).of(systemLanguage);
    return name == null ? displayName : capitalizeFirstLetter(name, locale);
  } catch {
    // Intl throws a RangeError for malformed language codes.
    return displayName;
  }
}

export function getConfiguredLanguageOptions(
  languages: InterfaceLanguageOptionDto[],
  defaultLanguage?: string
): Array<{ label: string; value: string }> {
  const languageList = getConfiguredLanguagesWithTenantDefault(
    languages,
    defaultLanguage
  );

  return languageList
    .map((language) => ({
      value: language.systemLanguage,
      label: getLanguageLabel(language),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, i18n.language));
}

export function getConfiguredLanguageCodes(
  languages: InterfaceLanguageOptionDto[],
  defaultLanguage?: string
): string[] {
  const languageList = getConfiguredLanguagesWithTenantDefault(
    languages,
    defaultLanguage
  );

  return languageList.map((language) => language.systemLanguage);
}

/**
 * Configured languages plus the tenant default when it is missing from that
 * list (for example localeMessages en with only de configured).
 */
function getConfiguredLanguagesWithTenantDefault(
  configuredLanguages: InterfaceLanguageOptionDto[],
  localeMessages?: string | null
): InterfaceLanguageOptionDto[] {
  if (
    configuredLanguages.length === 0 ||
    localeMessages == null ||
    localeMessages.length === 0
  ) {
    return configuredLanguages;
  }

  const defaultLanguage = localeMessages.split('-')[0];
  if (
    configuredLanguages.some(
      (language) => language.systemLanguage === defaultLanguage
    )
  ) {
    return configuredLanguages;
  }

  return [
    ...configuredLanguages,
    { systemLanguage: defaultLanguage, displayName: defaultLanguage },
  ];
}
